// Pure-TS fallback simulation. Mirrors the falling-sand subset of sim-core so
// the UI still works in environments where the wasm bundle isn't available
// (e.g. CI without wasm-pack, or before `npm run wasm:build` has run).

import type { MaterialDef, SandboxLike } from "./types";

// The fallback engine implements the basic falling-sand archetypes; the
// richer cloner / mite / rocket behaviours degrade to static here and
// only animate when the wasm sandbox is loaded.
export type Behavior =
  | "static"
  | "solid"
  | "powder"
  | "liquid"
  | "gas"
  | "cloner"
  | "mite"
  | "rocket"
  | "fuel"
  | "flame";

export class JsFallbackSandbox implements SandboxLike {
  private readonly w: number;
  private readonly h: number;
  private readonly grid: Uint16Array;
  private readonly seeds: Uint8Array;
  private readonly temp: Float32Array;
  private readonly behaviors: Behavior[];
  private readonly nameToId: Map<string, number>;
  private rng: () => number;
  private ticks = 0;

  constructor(width: number, height: number, materials: MaterialDef[], seed: number) {
    this.w = width;
    this.h = height;
    this.grid = new Uint16Array(width * height);
    this.seeds = new Uint8Array(width * height);
    this.temp = new Float32Array(width * height).fill(293.15);
    this.behaviors = materials.map((m) => (m.behavior ?? "static") as Behavior);
    this.nameToId = new Map(materials.map((m, i) => [m.id, i]));
    this.rng = mulberry32(seed >>> 0);
  }

  width() {
    return this.w;
  }
  height() {
    return this.h;
  }
  materialCount() {
    return this.behaviors.length;
  }
  materialId(id: string) {
    return this.nameToId.get(id) ?? 0;
  }
  tickCount() {
    return this.ticks;
  }

  paintCircle(cx: number, cy: number, r: number, material: number) {
    const r2 = r * r;
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dy * dy > r2) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= this.w || y >= this.h) continue;
        const i = y * this.w + x;
        this.grid[i] = material;
        this.seeds[i] = (Math.floor(this.rng() * 255) + 1) & 0xff;
      }
    }
  }

  heatCircle(cx: number, cy: number, r: number, kelvin: number) {
    const r2 = r * r;
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dy * dy > r2) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= this.w || y >= this.h) continue;
        this.temp[y * this.w + x] = kelvin;
      }
    }
  }

  tick(_dt: number) {
    this.fallingSandPass();
    this.diffuseTemperature(0.1);
    this.ticks++;
  }

  snapshotMaterials(): Uint16Array {
    return this.grid.slice();
  }
  snapshotSeeds(): Uint8Array {
    return this.seeds.slice();
  }
  snapshotTemperature(): Float32Array {
    return this.temp.slice();
  }

  runExperiment(_json: string): string {
    return "experiment runner requires the wasm sandbox";
  }

  snapshotExperiment(_name: string): string {
    return "snapshot requires the wasm sandbox";
  }

  clearAll(ambientK: number) {
    this.grid.fill(0);
    this.seeds.fill(0);
    this.temp.fill(ambientK);
  }

  setAmbientTemperature(ambientK: number) {
    // The fallback only tracks temperature, not material, so every
    // cell counts as "empty" here — fill the whole field. The wasm
    // path only re-targets empty cells to preserve mid-scene heat
    // sources, but the JS fallback is "no chemistry" anyway.
    this.temp.fill(ambientK);
  }

  toggleAt(_x: number, _y: number): boolean {
    // Fallback has no signal pass; nothing to toggle. The user
    // notices because the lamp swap that the wasm engine does
    // never lands.
    return false;
  }

  isTriggerAt(_x: number, _y: number): boolean {
    // Without a real signal pass, the hover overlay never fires
    // in fallback mode — same UX as not running into a switch.
    return false;
  }

  private fallingSandPass() {
    const w = this.w;
    const h = this.h;
    for (let y = h - 1; y >= 0; y--) {
      const leftFirst = this.rng() < 0.5;
      for (let i = 0; i < w; i++) {
        const x = leftFirst ? i : w - 1 - i;
        const idx = y * w + x;
        const m = this.grid[idx];
        if (m === 0) continue;
        const b = this.behaviors[m] ?? "static";
        // Static and fuel cells are immobile in the JS fallback. Cloner /
        // mite / rocket / fuel all live in dedicated passes that the JS
        // fallback doesn't implement; degrade them to static here.
        if (
          b === "static" ||
          b === "fuel" ||
          b === "flame" ||
          b === "cloner" ||
          b === "mite" ||
          b === "rocket"
        ) {
          continue;
        }
        if (b === "gas") {
          if (!this.tryMove(x, y, 0, -1)) this.tryMove(x, y, this.rng() < 0.5 ? -1 : 1, 0);
          continue;
        }
        if (this.tryMove(x, y, 0, 1)) continue;
        if (b === "powder" || b === "liquid") {
          const dir = this.rng() < 0.5 ? -1 : 1;
          if (this.tryMove(x, y, dir, 1)) continue;
          if (this.tryMove(x, y, -dir, 1)) continue;
        }
        if (b === "liquid") {
          const dir = this.rng() < 0.5 ? -1 : 1;
          if (this.tryMove(x, y, dir, 0)) continue;
          this.tryMove(x, y, -dir, 0);
        }
      }
    }
  }

  private tryMove(x: number, y: number, dx: number, dy: number): boolean {
    const nx = x + dx;
    const ny = y + dy;
    if (nx < 0 || ny < 0 || nx >= this.w || ny >= this.h) return false;
    const dst = ny * this.w + nx;
    if (this.grid[dst] !== 0) return false;
    const src = y * this.w + x;
    this.grid[dst] = this.grid[src];
    this.grid[src] = 0;
    return true;
  }

  private diffuseTemperature(coeff: number) {
    const w = this.w;
    const h = this.h;
    const out = new Float32Array(this.temp.length);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        const c = this.temp[i];
        const l = x === 0 ? c : this.temp[i - 1];
        const r = x === w - 1 ? c : this.temp[i + 1];
        const d = y === 0 ? c : this.temp[i - w];
        const u = y === h - 1 ? c : this.temp[i + w];
        out[i] = c + coeff * (l + r + d + u - 4 * c);
      }
    }
    this.temp.set(out);
  }
}

function mulberry32(seed: number): () => number {
  let a = seed || 1;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
